// 自前のお知らせ（トースト相当）の表示コンポーネント。
// 画面右下・全体MAP（ミニマップ）の上あたりに固定表示し、全レベル 10 秒で自動的に消える。
// キャンバス操作の邪魔にならないよう、コンテナ自体はポインタイベントを透過させる。

import { useEffect, type CSSProperties } from "react";
import { useNoticeStore, type Notice, type NoticeLevel } from "./notice-store";

// 自動消去までの時間（ms）。全レベル共通で、読み切れる長さとして 10 秒とする（利用者選択）。
const AUTO_DISMISS_MS = 10_000;

// レベル別の配色。控えめだが視認できる濃色背景＋白文字。
const LEVEL_COLOR: Record<NoticeLevel, string> = {
  info: "#334155",
  success: "#0f7b52",
  warning: "#b45309",
  error: "#b91c1c",
};

export function NoticeHost(): JSX.Element {
  const notices = useNoticeStore((s) => s.notices);
  const reset = useNoticeStore((s) => s.reset);
  // キャンバス離脱（アンマウント）時に残ったお知らせを消す（素早い canvas 切替での残留を防ぐ）。
  useEffect(() => reset, [reset]);

  const containerStyle: CSSProperties = {
    position: "fixed",
    // 全体MAP（ミニマップ約 124px ＋ズームボタン ＋余白）を避けて、その上あたりに出す。
    right: "16px",
    bottom: "200px",
    display: "flex",
    // 新しいお知らせを下（MAP に近い側）に、古いものを上へ積み上げる。
    flexDirection: "column-reverse",
    alignItems: "flex-end",
    gap: "8px",
    // コンテナはキャンバス操作を妨げないよう透過。各お知らせも表示のみで操作不要。
    pointerEvents: "none",
    maxWidth: "min(320px, 90vw)",
    zIndex: 20,
  };

  return (
    <div aria-hidden={notices.length === 0} style={containerStyle}>
      {notices.map((notice) => (
        <NoticeItem key={notice.id} notice={notice} />
      ))}
    </div>
  );
}

function NoticeItem({ notice }: { readonly notice: Notice }): JSX.Element {
  const dismiss = useNoticeStore((s) => s.dismiss);

  useEffect(() => {
    const timer = setTimeout(() => dismiss(notice.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [notice.id, dismiss]);

  const style: CSSProperties = {
    padding: "8px 12px",
    borderRadius: "10px",
    background: LEVEL_COLOR[notice.level],
    color: "#ffffff",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.22)",
    fontSize: "0.82rem",
    fontWeight: 600,
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxWidth: "100%",
  };

  return (
    <div
      // 警告・エラーは alert、情報・成功は status として読み上げる。
      role={notice.level === "warning" || notice.level === "error" ? "alert" : "status"}
      aria-live="polite"
      style={style}
    >
      {notice.message}
    </div>
  );
}
